import React from "react";
import { Form, Field } from "react-final-form";
import { FORM_ERROR } from "final-form";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import { Dialog, DialogDescription, DialogTitle } from "components/ui/dialog";
import { DialogCard } from "components/ui/dialog-card";
import { Textarea } from "components/Form/Field";
import StarRatingInput from "components/ui/star-rating-input";
import { useCreateNote } from "hooks/note";
import { getServerMessage } from "lib/serverError";
import type { BookingInterface } from "types/booking";
import { Role } from "types/user";

type RateProviderDialogProps = {
    booking: BookingInterface;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

type FormValues = {
    note: number;
    comment: string;
};

const RateProviderDialog: React.FC<RateProviderDialogProps> = ({ booking, open, onOpenChange }) => {
    const intl = useIntl();
    const queryClient = useQueryClient();
    const createNoteMutation = useCreateNote();

    const validate = (values: FormValues) => {
        const errors: Partial<Record<keyof FormValues, string>> = {};

        if (!values.note || values.note < 1) {
            errors.note = intl.formatMessage({ id: "profile.service_history.rating_modal.error.rating_required" });
        }

        return errors;
    };

    const onSubmit = async (values: FormValues) => {
        try {
            await createNoteMutation.mutateAsync({
                note: values.note,
                comment: values.comment.trim(),
                booking_id: booking.id,
                user_type: Role.PROVIDER,
                user_id: booking.provider_id,
            });
            toast.success(intl.formatMessage({ id: "profile.service_history.rating_modal.success" }));
            queryClient.invalidateQueries({ queryKey: ["profile", "service-history"] });
            onOpenChange(false);
        } catch (err: unknown) {
            return {
                [FORM_ERROR]: getServerMessage(err)
                    ?? intl.formatMessage({ id: "profile.service_history.rating_modal.error.generic" }),
            };
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogCard closeLabel={intl.formatMessage({ id: "profile.service_history.rating_modal.close" })}>
                <Form<FormValues>
                    initialValues={{ note: 0, comment: "" }}
                    validate={validate}
                    onSubmit={onSubmit}
                    render={({ handleSubmit, submitting, submitError }) => (
                        <form className="rating-modal-form flex flex-col gap-6" onSubmit={handleSubmit}>
                            <div>
                                <DialogTitle className="text-xl font-bold text-slate-900">
                                    {intl.formatMessage({ id: "profile.service_history.rating_modal.title" })}
                                </DialogTitle>
                                <DialogDescription className="mt-2 text-sm text-slate-500">
                                    {intl.formatMessage({ id: "profile.service_history.rating_modal.subtitle" })}
                                </DialogDescription>
                            </div>

                            <Field<number> name="note">
                                {({ input, meta }) => (
                                    <div className="flex flex-col gap-3">
                                        <label className="text-sm font-medium text-slate-900">
                                            {intl.formatMessage({ id: "profile.service_history.rating_modal.rating_label" })}
                                        </label>
                                        <StarRatingInput
                                            value={input.value}
                                            onChange={input.onChange}
                                            aria-label={intl.formatMessage({
                                                id: "profile.service_history.rating_modal.rating_label",
                                            })}
                                        />
                                        {meta.touched && meta.error ? (
                                            <p className="text-sm font-medium text-destructive">{meta.error}</p>
                                        ) : null}
                                    </div>
                                )}
                            </Field>

                            <hr className="border-slate-200" />

                            <Textarea
                                inputProps={{
                                    id: "rate-provider-comment",
                                    name: "comment",
                                    maxLength: 255,
                                    placeholder: intl.formatMessage({
                                        id: "profile.service_history.rating_modal.comment_placeholder",
                                    }),
                                }}
                                labelProps={{
                                    label: intl.formatMessage({
                                        id: "profile.service_history.rating_modal.comment_label",
                                    }),
                                }}
                            />

                            <div aria-live="polite">
                                {submitError ? (
                                    <p className="text-sm font-medium text-destructive">{submitError}</p>
                                ) : null}
                            </div>

                            <Button
                                type="submit"
                                disabled={submitting}
                                className="h-12 w-full rounded-xl text-base font-bold"
                            >
                                {intl.formatMessage({ id: "profile.service_history.rating_modal.submit" })}
                            </Button>
                        </form>
                    )}
                />
            </DialogCard>
        </Dialog>
    );
};

export default RateProviderDialog;
